import _                                    from 'underscore';
import React, { FC, ReactNode, useContext } from 'react';
import { Cell, Grid }                       from 'styled-css-grid';

import { CorvalAccountLike, CorvalAuditEntry }         from '../../../types';
import { UserService }                                 from '../../../services';
import { ProjectContext }                              from '../../pages/projects/ProjectIndexPage';
import { UserAvatar }                                  from '../user-avatar/UserAvatar';
import { OrganizationUser }                            from '../user/OrganizationUser';
import { SystemAdminsOnly }                            from '../util/access/RoleAccessControl';
import { AccountUser }                                 from '../user/AccountUser';
import { AuditMemoStatement, AuditMemoStatementProps } from './AuditMemoStatement';
import { useAuditRecords }                             from '../../../hooks';

export interface AuditMemoProps extends Omit<AuditMemoStatementProps, 'timestampUtc'> {
	record: CorvalAuditEntry,
	organizationId?: string,
	includeAvatar?: boolean,
	alignAvatarMiddle?: boolean,
	containsAuditDataChangesOn?: string[]
	
	render?( props: AuditMemoStatementProps, record?: CorvalAuditEntry ): ReactNode,
}

const auditMemoPropsDefaultProps: AuditMemoProps = {
	record:                     null,
	organizationId:             null,
	includeAvatar:              true,
	alignAvatarMiddle:          true,
	render:                     null,
	containsAuditDataChangesOn: null,
}

/* Component that reads an audit record, acquires the owner of the entry, and then either displays a standard memo statement or a custom rendered memo component */
const AuditMemo: FC<AuditMemoProps> = ( props ) => {
	
	let { containsAuditDataChangesOn }                               = props,
	    { extractValueFromChangeEntry, valuesFromChangeEntryDiffer } = useAuditRecords(),
	    projectContext                                               = useContext( ProjectContext ),
	    organizationId                                               = projectContext.organizationId ?? props.organizationId ?? null;
	
	if( !props.record ) return null;
	
	if( props.record?.auditData && !_.isNull( containsAuditDataChangesOn ) ){
		
		let auditData = JSON.parse( props.record.auditData );
		
		// for each key in the audit data, compare the values
		let changes = containsAuditDataChangesOn.map( auditDataAttribute => {
			
			let [ newValue, oldValue ] = auditData[ auditDataAttribute ]
			                             ? extractValueFromChangeEntry( auditData[ auditDataAttribute ] )
			                             : [ null, null ];
			
			return valuesFromChangeEntryDiffer( [ newValue, oldValue ] );
		} );
		
		if( _.compact( changes ).length == 0 ) return null;
	}
	
	let statementProps = {
		timestampUtc: props.record.timestampUtc,
		..._.pick( props,
			'userLabel',
			'preamble',
			'actionDesc',
			'actionVerb',
			'auditSubject',
			'auditAdditionalInfo',
			'timestampFormat',
			'includeTimestampInMessage',
		)
	}
	
	const MemoComponent: FC<AuditMemoStatementProps> = ( memoProps ) => {
		
		return ( _.isFunction( props.render ) )
		       ? <>{ props.render( memoProps, props.record ) }</>
		       : <AuditMemoStatement { ...memoProps }/>
	}
	
	const memoWithUserInfo = ( user: CorvalAccountLike, statementProps: AuditMemoStatementProps ) => (
		
		<Grid columns={ `auto 1fr` }>
			<Cell middle={ props.alignAvatarMiddle }>{ props.includeAvatar && <UserAvatar user={ user } asInitials={ true } withName={ false } size={ 'nano' }/> }</Cell>
			<Cell middle={ props.alignAvatarMiddle }><MemoComponent userLabel={ UserService.getShortUserName( user ) }{ ...statementProps }/></Cell>
		</Grid>
	)
	
	return (
		
		<>
			{ organizationId ? (
				
				/* If given an organization */
				<OrganizationUser
					accountId={ props.record.accountId }
					organizationId={ organizationId }
					noItemComponent={ <MemoComponent { ...statementProps }/> }>
					
					{ ( user: CorvalAccountLike ) => memoWithUserInfo( user, statementProps ) }
				
				</OrganizationUser>
			
			) : (
				
				  /* If allowed to see any organization */
				  <SystemAdminsOnly replaceWith={ <MemoComponent { ...statementProps }/> }>
					
					  <AccountUser
						  accountId={ props.record.accountId }
						  noItemComponent={ <MemoComponent { ...statementProps }/> }>
						
						  { ( user: CorvalAccountLike ) => memoWithUserInfo( user, statementProps ) }
					
					  </AccountUser>
				
				  </SystemAdminsOnly>
			  ) }
		</>
	);
};

AuditMemo.defaultProps = auditMemoPropsDefaultProps;

export { AuditMemo, AuditMemoStatement }
